import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { updateSnackbar } from '../../_redux/SnackbarSlice';
import {
  getInternalExternalObj,
  isDefaultText,
} from '../../_utilities/internalexternalhelper';

import './InternalExternalTableStyle.css';

const InternalExternalTable = (props) => {
  const {
    customValues,
    isEdit = false,
    onDisableChange, // tells parent if duplicates exist
    updateCustomValues: propsUpdateCustomValues,
  } = props;

  const [ieObjectList, setIEObjList] = useState([]);
  const [duplicateField, setDuplicateField] = useState(null); // e.g. "internal-1", "external-2"
  const [hasDuplicate, setHasDuplicate] = useState(false); // true = some duplicate exists
  const dispatch = useDispatch();

  // Helper: send data back to parent WITHOUT isDefault
  const syncToParent = (list) => {
    const plainList = list.map((item) => ({
      internal: item.internal,
      external: item.external,
    }));

    if (typeof propsUpdateCustomValues === 'function') {
      propsUpdateCustomValues(plainList);
    } else if (typeof props.updateCustomValues === 'function') {
      props.updateCustomValues(plainList);
    }
  };

  // When customValues change from parent, rebuild list but
  // PRESERVE existing isDefault flags when possible
  useEffect(() => {
    const internalExternalObj = getInternalExternalObj(customValues);

    setIEObjList((prev) => {
      let listWithFlags;

      if (prev.length === internalExternalObj.length) {
        // Same length → keep previous isDefault per index
        listWithFlags = internalExternalObj.map((item, idx) => ({
          ...item,
          isDefault: prev[idx]?.isDefault ?? isDefaultText(item.internal),
        }));
      } else {
        // Length changed (e.g. external reset) → fallback to isDefaultText
        listWithFlags = internalExternalObj.map((item) => ({
          ...item,
          isDefault: isDefaultText(item.internal),
        }));
      }

      recalculateDuplicates(listWithFlags);
      return listWithFlags;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customValues]);

  // Notify parent (CustomDynamicTable) whenever duplicate state changes
  useEffect(() => {
    if (typeof onDisableChange === 'function') {
      onDisableChange(hasDuplicate);
    }
  }, [hasDuplicate, onDisableChange]);

  // ---------- DUPLICATE HELPERS ----------

  // Check if a specific value is duplicate, ignoring current index
  const isDuplicateForField = (value, currentIndex, fieldType) => {
    if (!value?.trim()) return false;

    const val = value.trim().toLowerCase();
    return ieObjectList.some((item, idx) => {
      if (idx === currentIndex) return false;
      const fieldValue =
        fieldType === 'internal' ? item.internal : item.external;
      return fieldValue?.trim().toLowerCase() === val;
    });
  };

  // Check if overall list has *any* duplicate internal/external
  const hasAnyDuplicate = (list) => {
    const collect = (arr) =>
      arr.map((v) => v?.trim().toLowerCase()).filter((v) => v && v.length > 0);

    const internals = collect(list.map((i) => i.internal));
    const externals = collect(list.map((i) => i.external));

    const hasDupArr = (arr) => {
      const set = new Set();
      for (const v of arr) {
        if (set.has(v)) return true;
        set.add(v);
      }
      return false;
    };

    return hasDupArr(internals) || hasDupArr(externals);
  };

  // Find the first duplicate field key to highlight (like "internal-1" or "external-2")
  const findFirstDuplicateKey = (list) => {
    // internal
    const seenInt = new Map();
    for (let i = 0; i < list.length; i++) {
      const v = list[i].internal?.trim().toLowerCase();
      if (!v) continue;
      if (seenInt.has(v)) {
        return `internal-${i}`;
      }
      seenInt.set(v, i);
    }

    // external
    const seenExt = new Map();
    for (let i = 0; i < list.length; i++) {
      const v = list[i].external?.trim().toLowerCase();
      if (!v) continue;
      if (seenExt.has(v)) {
        return `external-${i}`;
      }
      seenExt.set(v, i);
    }

    return null;
  };

  // Recalculate duplicate state whenever list changes
  const recalculateDuplicates = (list) => {
    const anyDup = hasAnyDuplicate(list);
    const firstKey = anyDup ? findFirstDuplicateKey(list) : null;

    setHasDuplicate(anyDup);
    setDuplicateField(firstKey);
  };

  // ---------- CHANGE HANDLERS ----------

  const onChangeInternal = (value, index) => {
    const list = [...ieObjectList];
    list[index].internal = value; // do NOT change isDefault

    // specific field duplicate check → snackbar
    const isDupField = isDuplicateForField(value, index, 'internal');
    if (isDupField) {
      dispatch(
        updateSnackbar({
          type: 'error',
          message: 'Duplicates are not allowed',
        })
      );
    }

    setIEObjList(list);
    recalculateDuplicates(list); // global duplicate state
  };

  const onChangeExternal = (value, index) => {
    const list = [...ieObjectList];
    list[index].external = value; // do NOT change isDefault

    const isDupField = isDuplicateForField(value, index, 'external');
    if (isDupField) {
      dispatch(
        updateSnackbar({
          type: 'error',
          message: 'Duplicates are not allowed',
        })
      );
    }

    setIEObjList(list);
    recalculateDuplicates(list);
  };

  const onkeyhandlerhandler = () => {
    // Only update if no duplicates
    if (!hasDuplicate) {
      syncToParent(ieObjectList);
    }
  };

  const addMore = () => {
    const list = [...ieObjectList];
    list.push({ internal: '', external: '', isDefault: false });
    setIEObjList(list);
    syncToParent(list);
    recalculateDuplicates(list);
  };

  const deleteIEObj = (index) => {
    const list = [...ieObjectList];
    list.splice(index, 1);
    setIEObjList(list);
    syncToParent(list);
    recalculateDuplicates(list); // ✅ if duplicates remain, keep disabled; if not, re-enable
  };

  // ---------- RENDER HELPERS ----------

  const internalComp = (ieObj, index) => {
    if (isEdit && !ieObj.isDefault) {
      const isDupField =
        duplicateField === `internal-${index}` ||
        duplicateField === `external-${index}`;
      return (
        <TextField
          id="internal-input"
          error={ieObj?.internal?.trim().length === 0 || isDupField}
          value={ieObj.internal}
          onChange={(e) => onChangeInternal(e.target.value, index)}
          onBlur={onkeyhandlerhandler}
        />
      );
    }
    return ieObj.internal;
  };

  const externalComp = (ieObj, index) => {
    if (isEdit && !ieObj.isDefault) {
      const isDupField =
        duplicateField === `external-${index}` ||
        duplicateField === `internal-${index}`;
      return (
        <TextField
          id="external-input"
          error={ieObj?.external?.trim().length === 0 || isDupField}
          value={ieObj.external}
          onChange={(e) => onChangeExternal(e.target.value, index)}
          onBlur={onkeyhandlerhandler}
        />
      );
    }
    return ieObj.external;
  };

  return (
    <div className="internal-external-wrapper">
      <div className="internal-external-wrapper_headers">
        <div className="internal-external-wrapper_headers_internal">
          Internal
        </div>
        <div className="internal-external-wrapper_headers_relation"></div>
        <div className="internal-external-wrapper_headers_external">
          External
        </div>
        {isEdit && (
          <div className="internal-external-wrapper_headers_actions"></div>
        )}
      </div>
      {ieObjectList.map((ieObj, index) => (
        <div
          className="internal-external-wrapper_body"
          key={`internal-external-${index}`}
        >
          <div className="internal-external-wrapper_body_internal">
            {internalComp(ieObj, index)}
          </div>
          <div className="internal-external-wrapper_body_relation">
            <img
              src={process.env.PUBLIC_URL + '/assests/img/link.svg'}
              alt="link"
              className="internal-external-wrapper_body_relation_svg"
            />
          </div>
          <div className="internal-external-wrapper_body_external">
            {externalComp(ieObj, index)}
          </div>
          {isEdit && (
            <div className="internal-external-wrapper_body_actions">
              {/* Default rows: no delete icon */}
              {!ieObj.isDefault && (
                <DeleteIcon onClick={() => deleteIEObj(index)} />
              )}
            </div>
          )}
        </div>
      ))}

      {isEdit && (
        <div className="internal-external-wrapper_add_more">
          <Button variant="text" disabled={hasDuplicate} onClick={addMore}>
            + Add more
          </Button>
        </div>
      )}
    </div>
  );
};

export default memo(InternalExternalTable);
